import { useMemo } from "react";
import { baueListenIndex, eindeutigeWerte, flaschenVonWein, hatRebsorte, rebsortenListe, rebsortenNamen, regionZuWein, reifeVonWein, sortiere, trinkabVonWein, trinkbisVonWein, winzerZuWein } from "../kern/fachlogik.js";
import { passtEinerWahl, passtWahl, passtWahlMit } from "../kern/filterwahl.js";
import { jahrLabel, sortDe } from "../kern/format.js";
import { SORT_OPTIONEN, TYPEN } from "../kern/konstanten.js";
import { useEntprellt, useNachwachsen, useWachsendeElemente, usePersistentState } from "../ui/hooks.jsx";
import { MehrfachAuswahl } from "../ui/mehrfachauswahl.jsx";
import { Auswahl, Btn, JahrgangsLinks, Leer, TextKnopf, TypPunkt, Werkzeugleiste, ZeilenMenue } from "../ui/primitive.jsx";
import { t } from "../i18n/index.js";
import { landName } from "../i18n/laender.js";

const REIFEN = [["jung", "Noch nicht trinkreif"], ["reif", "Trinkreif"], ["ueber", "Überfällig"]];

function WeineAnsicht({ db, nurBestand, geheZu, oeffneModal, loescheWein }) {
  const [suche, setSuche] = usePersistentState("weine.suche", "");
  /* Eingabe sofort, Filterung kurz danach — siehe useEntprellt. */
  const sucheAktiv = useEntprellt(suche, 150);
  const [typFilter, setTypFilter] = usePersistentState("weine.typFilter", []);
  const [landFilter, setLandFilter] = usePersistentState("weine.landFilter", []);
  const [regionFilter, setRegionFilter] = usePersistentState("weine.regionFilter", []);
  const [winzerFilter, setWinzerFilter] = usePersistentState("weine.winzerFilter", []);
  const [rebsorteFilter, setRebsorteFilter] = usePersistentState("weine.rebsorteFilter", []);
  const [reifeFilter, setReifeFilter] = usePersistentState("weine.reifeFilter", []);
  const [lagerFilter, setLagerFilter] = usePersistentState("weine.lagerFilter", []);
  const [jahrgangFilter, setJahrgangFilter] = usePersistentState("weine.jahrgangFilter", []);
  const [favoritFilter, setFavoritFilter] = usePersistentState("weine.favoritFilter", "");
  const [labelFilter, setLabelFilter] = usePersistentState("weine.labelFilter", []);
  /* Mehrfachwahlen (Liste, leer heißt alle) — außer „Lieblingswein",
     das ein Ja/Nein ist. Siehe kern/filterwahl.js. */
  const [sortierung, setSortierung] = usePersistentState("weine.sortierung", "name-az");
  /* ── Auswahllisten, kaskadierend ─────────────────────────────
     Die Optionen eines Filters kommen aus den Weinen, die alle
     ÜBRIGEN Filter passieren — der Filter selbst zählt nicht mit.
     Mit „Land: Deutschland“ bleiben bei Region nur deutsche Regionen,
     die Länderliste selbst bleibt vollständig. Dieselbe Regel wie in
     ui/weinfilter.jsx, hier auf die weinweise Sicht angewandt. */
  const basis = useMemo(
    () => (nurBestand ? db.weine.filter((w) => w.nurKatalog || flaschenVonWein(w) > 0) : db.weine),
    [db.weine, nurBestand]);

  const passtOhne = (wein, ausser) => {
    const wz = winzerZuWein(db, wein);
    const rg = regionZuWein(db, wein);
    return passtWeinOhne(wein, wz, rg, ausser);
  };

  /* Weinweise: Jahrgang und Lagerort treffen, wenn EINER der
     Jahrgänge passt. Mehrere gewählte Werte verbinden sich mit ODER. */
  const passtWeinOhne = (wein, wz, rg, ausser) => {
    if (ausser !== "typ" && !passtWahl(typFilter, wein.typ)) return false;
    if (ausser !== "land" && !passtWahl(landFilter, rg ? (rg.land || "Ohne Land") : "Ohne Land")) return false;
    if (ausser !== "region" && !passtWahl(regionFilter, rg ? rg.id : null)) return false;
    if (ausser !== "winzer" && !passtWahl(winzerFilter, wz ? wz.id : null)) return false;
    if (ausser !== "rebsorte" && !passtWahlMit(rebsorteFilter, (r) => hatRebsorte(wein, r))) return false;
    if (ausser !== "reife" && !passtWahl(reifeFilter, reifeVonWein(wein))) return false;
    if (ausser !== "lager" && !passtEinerWahl(lagerFilter, wein.jahrgaenge.map((j) => j.lagerort || "Ohne Lagerort"))) return false;
    if (ausser !== "jahrgang" && !passtEinerWahl(jahrgangFilter, wein.jahrgaenge.map((j) => j.jahr))) return false;
    if (ausser !== "favorit" && favoritFilter && !wein.favorit) return false;
    if (ausser !== "label" && !passtWahl(labelFilter, wein.label || "")) return false;
    return true;
  };

  const sig = JSON.stringify([typFilter, landFilter, regionFilter, winzerFilter, rebsorteFilter,
    reifeFilter, lagerFilter, jahrgangFilter, favoritFilter, labelFilter, nurBestand]);

  const sammle = (ausser, hole) => {
    const set = new Set();
    for (const wein of basis) { if (passtOhne(wein, ausser)) hole(set, wein); }
    return set;
  };
  const sortiert = (set) => [...set].sort((a, b) => String(a).localeCompare(String(b), "de"));

  /* eslint-disable react-hooks/exhaustive-deps */
  const rebsorten = useMemo(() =>
    sortiert(sammle("rebsorte", (m, w) => rebsortenListe([w]).forEach((r) => m.add(r)))), [basis, sig]);
  const labels = useMemo(() =>
    sortiert(sammle("label", (m, w) => { if (w.label) m.add(w.label); })), [basis, sig]);
  const laender = useMemo(() => sortiert(sammle("land", (m, w) => {
    const r = regionZuWein(db, w); m.add(r ? (r.land || "Ohne Land") : "Ohne Land");
  })), [basis, sig]);
  const lagerorte = useMemo(() => sortiert(sammle("lager", (m, w) => {
    for (const j of w.jahrgaenge) if (j.lagerort) m.add(j.lagerort);
  })), [basis, sig]);
  const jahrgaengeFilter = useMemo(() => [...sammle("jahrgang", (m, w) => {
    for (const j of w.jahrgaenge) if (j.jahr != null) m.add(j.jahr);
  })].sort((a, b) => b - a), [basis, sig]);
  const winzerIds = useMemo(() => sammle("winzer", (m, w) => m.add(w.winzerId)), [basis, sig]);
  const regionIds = useMemo(() => sammle("region", (m, w) => {
    const r = regionZuWein(db, w); if (r) m.add(r.id);
  }), [basis, sig]);
  const typen = useMemo(() => sammle("typ", (m, w) => { if (w.typ) m.add(w.typ); }), [basis, sig]);
  const reifen = useMemo(() => sammle("reife", (m, w) => {
    const r = reifeVonWein(w); if (r) m.add(r);
  }), [basis, sig]);
  /* eslint-enable react-hooks/exhaustive-deps */

  /* Filtern, projizieren und sortieren stand bisher offen im
     Rumpf — und lief damit bei JEDEM Render, auch wenn sich nur ein
     unbeteiligter Zustand geändert hatte. Bei 1200 Weinen sind das
     jedes Mal eine Projektion samt Bewertungs- und Einlagerungssuche
     je Zeile. `keller.jsx` hatte das längst gekapselt, hier fehlte es.

     Die Abhängigkeiten sind absichtlich einzeln aufgeführt und nicht
     als Signaturkette: So sieht man beim Lesen, wovon die Liste
     wirklich abhängt. */
  const weine = useMemo(() => {
    const index = baueListenIndex(db);
    const q = sucheAktiv.trim().toLowerCase();
    const gefiltert = db.weine.filter((wein) => {
      /* Katalogweine haben nie Flaschen — sie gehören ja niemandem
         hier. Der Schalter „Nur mit Bestand“ meint den eigenen
         Keller; auf die Katalogsicht angewandt bliebe sie leer. */
      if (nurBestand && !wein.nurKatalog && flaschenVonWein(wein) === 0) return false;
      const winzer = winzerZuWein(db, wein);
      const region = regionZuWein(db, wein);
      if (!passtWeinOhne(wein, winzer, region, null)) return false;
      if (q) {
        const heu = `${wein.name} ${winzer ? winzer.name : ""} ${region ? region.name : ""} ${wein.rebsorten || ""} ${wein.label || ""}`.toLowerCase();
        if (!heu.includes(q)) return false;
      }
      return true;
    });

    const projWein = (wein) => {
      const jahre = wein.jahrgaenge.map((j) => j.jahr);
      const jahrMax = jahre.length ? Math.max(...jahre) : 0;
      const jahrMin = jahre.length ? Math.min(...jahre) : 0;
      const neuster = wein.jahrgaenge.find((j) => j.jahr === jahrMax);
      return {
        name: wein.name, jahrMin, jahrMax,
        preis: neuster && neuster.preis != null ? neuster.preis : null,
        bewertung: index.bewertungWein(wein.id),
        bestand: flaschenVonWein(wein),
        trinkab: trinkabVonWein(wein),
        trinkbis: trinkbisVonWein(wein),
        einlagerung: index.einlagerung(wein.id),
      };
    };
    return sortiere(gefiltert, projWein, sortierung);
  }, [db, nurBestand, sortierung, sucheAktiv, typFilter, landFilter, regionFilter, winzerFilter,
      rebsorteFilter, reifeFilter, lagerFilter, jahrgangFilter, favoritFilter, labelFilter]);

  const filterAktiv = !!(typFilter.length || landFilter.length || regionFilter.length || winzerFilter.length
    || rebsorteFilter.length || reifeFilter.length || lagerFilter.length || jahrgangFilter.length
    || favoritFilter || labelFilter.length);
  const zuruecksetzen = () => {
    setTypFilter([]); setLandFilter([]); setRegionFilter([]); setWinzerFilter([]); setRebsorteFilter([]);
    setReifeFilter([]); setLagerFilter([]); setJahrgangFilter([]); setFavoritFilter(""); setLabelFilter([]);
  };
  const regionName = (id) => (db.regions.find((r) => r.id === id) || {}).name || id;
  const winzerName = (id) => (db.winzer.find((w) => w.id === id) || {}).name || id;
  const landLabel = (l) => (l === "Ohne Land" ? t("Ohne Land") : landName(l));

  const { sichtbar, marke, alles, gesamt } = useNachwachsen(weine);

  /* Fertige Zeilen merken und beim Nachwachsen nur den Zuwachs
     bauen. Siehe useWachsendeElemente. */
  const zeilenElemente = useWachsendeElemente(weine, sichtbar,
    (wein) => {
                const winzer = winzerZuWein(db, wein);
                const region = regionZuWein(db, wein);
                return (
                  <tr key={wein.id}>
                    <td><button className="wl-zeilentitel" onClick={() => geheZu({ view: "wein", id: wein.id })}>{wein.name}{wein.favorit && <span className="wl-favorit" title={t("Lieblingswein")}>★</span>}</button></td>
                    <td>{winzer ? <TextKnopf onClick={() => geheZu({ view: "winzerDetail", id: winzer.id })}>{winzer.name}</TextKnopf> : "—"}</td>
                    <td>{region && region.land ? landName(region.land) : "—"}</td>
                    <td>{region ? <TextKnopf onClick={() => geheZu({ view: "region", id: region.id })}>{region.name}</TextKnopf> : "—"}</td>
                    <td><TypPunkt typ={wein.typ} /></td>
                    <td>{rebsortenNamen(wein) || "—"}</td>
                    <td><JahrgangsLinks wein={wein} nurBestand={nurBestand} geheZu={geheZu} /></td>
                    <td className="num">{flaschenVonWein(wein)}</td>
                    <td className="wl-rechts">
                      <ZeilenMenue beschriftung={`Aktionen für ${wein.name}`} eintraege={[
                        { label: "Öffnen", onClick: () => geheZu({ view: "wein", id: wein.id }) },
                        { label: "Bearbeiten", onClick: () => oeffneModal({ typ: "wein", objekt: wein }) },
                        { label: "Wein löschen", gefahr: true, onClick: () => loescheWein(wein.id) },
                      ]} />
                    </td>
                  </tr>
                );
    },
    [db, nurBestand, geheZu, oeffneModal, loescheWein]);

  return (
    <>
      <Werkzeugleiste
        suche={suche} setSuche={setSuche} sucheLabel={t("Weine durchsuchen …")}
        filterAktiv={filterAktiv}
        onReset={zuruecksetzen}
        sortOptionen={SORT_OPTIONEN} sortWert={sortierung} setSort={setSortierung}
        aktion={<Btn variante="voll" onClick={() => oeffneModal({ typ: "wein" })}>{t("Wein anlegen")}</Btn>}
        filter={
          <>
            <Auswahl value={favoritFilter} onChange={(e) => setFavoritFilter(e.target.value)} aria-label={t("Nach Lieblingsweinen filtern")}>
              <option value="">{t("Alle Weine")}</option>
              <option value="1">{t("Nur Lieblingsweine")}</option>
            </Auswahl>
            <MehrfachAuswahl wahl={typFilter} setWahl={setTypFilter} alleText={t("Alle Typen")} beschriftung={t("Nach Weintyp filtern")}
              optionen={Object.entries(TYPEN).filter(([k]) => typen.has(k)).map(([k, typ]) => ({ wert: k, label: t(typ.label) }))}
              beschrifte={(k) => (TYPEN[k] ? t(TYPEN[k].label) : k)} />
            <MehrfachAuswahl wahl={jahrgangFilter} setWahl={setJahrgangFilter} alleText={t("Alle Jahrgänge")} beschriftung={t("Nach Jahrgang filtern")}
              optionen={jahrgaengeFilter.map((j) => ({ wert: String(j), label: jahrLabel(j) }))}
              beschrifte={(j) => jahrLabel(Number(j))} />
            <MehrfachAuswahl wahl={landFilter} setWahl={setLandFilter} alleText={t("Alle Länder")} beschriftung={t("Nach Land filtern")}
              optionen={laender.map((l) => ({ wert: l, label: landLabel(l) }))} beschrifte={landLabel} />
            <MehrfachAuswahl wahl={regionFilter} setWahl={setRegionFilter} alleText={t("Alle Regionen")} beschriftung={t("Nach Region filtern")}
              optionen={[...db.regions].filter((r) => regionIds.has(r.id)).sort(sortDe).map((r) => ({ wert: r.id, label: r.name }))}
              beschrifte={regionName} />
            <MehrfachAuswahl wahl={winzerFilter} setWahl={setWinzerFilter} alleText={t("Alle Winzer")} beschriftung={t("Nach Winzer filtern")}
              optionen={[...db.winzer].filter((w) => winzerIds.has(w.id)).sort(sortDe).map((w) => ({ wert: w.id, label: w.name }))}
              beschrifte={winzerName} />
            <MehrfachAuswahl wahl={rebsorteFilter} setWahl={setRebsorteFilter} alleText={t("Alle Rebsorten")} beschriftung={t("Nach Rebsorte filtern")}
              optionen={rebsorten.map((r) => ({ wert: r, label: r }))} />
            <MehrfachAuswahl wahl={reifeFilter} setWahl={setReifeFilter} alleText={t("Jede Trinkreife")} beschriftung={t("Nach Trinkreife filtern")}
              optionen={REIFEN.filter(([k]) => reifen.has(k)).map(([k, lbl]) => ({ wert: k, label: t(lbl) }))}
              beschrifte={(k) => t((REIFEN.find(([x]) => x === k) || [k, k])[1])} />
            {(labels.length > 0 || labelFilter.length > 0) && (
              <MehrfachAuswahl wahl={labelFilter} setWahl={setLabelFilter} alleText={t("Alle Labels")} beschriftung={t("Nach Label filtern")}
                optionen={labels.map((l) => ({ wert: l, label: l }))} />
            )}
            {(lagerorte.length > 0 || lagerFilter.length > 0) && (
              <MehrfachAuswahl wahl={lagerFilter} setWahl={setLagerFilter} alleText={t("Alle Lagerorte")} beschriftung={t("Nach Lagerort filtern")}
                optionen={lagerorte.map((l) => ({ wert: l, label: l }))}
                beschrifte={(l) => (l === "Ohne Lagerort" ? t("Ohne Lagerort") : l)} />
            )}
          </>
        }
      />
      {weine.length === 0 ? (
        <Leer text={t("Keine Weine gefunden. Lege einen Wein an oder passe Suche und Filter an.")}
          aktion={<Btn onClick={() => oeffneModal({ typ: "wein" })}>{t("Wein anlegen")}</Btn>} />
      ) : (
        <div className="wl-scroll">
          <table className="wl-tabelle">
            <thead>
              <tr><th>{t("Wein")}</th><th>{t("Winzer")}</th><th>{t("Land")}</th><th>{t("Region")}</th><th>{t("Typ")}</th><th>{t("Rebsorten")}</th><th>{t("Jahrgänge")}</th><th className="num">{t("Flaschen")}</th><th className="wl-rechts"> </th></tr>
            </thead>
            <tbody>{zeilenElemente}
            </tbody>
          </table>
        </div>
      )}

      {/* Anker fürs Nachwachsen. Die Zeile sagt zugleich, dass mehr da
          ist als gerade sichtbar — sonst hielte man die Liste für
          vollständig. Steht außerhalb des waagerecht schiebbaren
          Bereichs, damit sie beim Schieben nicht mitwandert. */}
      {!alles && (
        <div className="wl-nachwachsen" ref={marke}>
          {t("{n} von {m} angezeigt — weiter scrollen für mehr", { n: sichtbar.length, m: gesamt })}
        </div>
      )}
    </>
  );
}

export { WeineAnsicht };
